<template>
  <dd-modal
      :visible="visible"
      :show-cancel="true"
      @cancel="$emit('cancel')"
      width="52vw"
      data-dusk="select-document-modal"
  >
    <dd-header-card header="Documenten selecteren">
      <h3 v-if="!loading && noDocuments" class="text-center color-black mt-xs">
        Er zijn geen documenten beschikbaar voor deze klant
      </h3>

      <div v-else class="flex-row gap-md mt-md" style="align-items: flex-start !important">
        <div class="flex-col gap-md" style="min-width: 220px">
          <dd-button
              v-for="(categoryName, key) in AttachableCategoriesDisplayEnumByCount"
              :type="categoryState === key ? 'primary' : 'none'"
              class="width-stretch"
              :key="key"
              @click="handleCategoryChange( key )"
              style="position: relative"
          >
            {{ categoryName }}
            <dd-icon
                v-if="countSelectedByCategory[key]"
                icon="mdi mdi-check"
                size="md"
                :class="['category-check', categoryState === key ? 'color-white' : 'color-black']"
            />
          </dd-button>
        </div>

        <div class="flex-stretch flex-col gap-md" style="max-width: calc(100% - 240px)">
          <a-tabs
              :defaultActiveKey="0"
              mode="horizontal"
              tab-position="top"
              @prevClick="() => {}"
              @nextClick="() => {}"
          >
            <a-tab-pane
                v-for="(customer, index) in attachableData"
                :key="index"
                :tab="fullName(customer.customer)"
            >
              <dd-collapse
                :activeKey="defaultPanelKey"
              >
                <a-collapse-panel
                  v-for="entity in customer.entities"
                  :key="'attachableEntity.' + entity.id"
                  :header="entity.name"
                  :class="{'inactive': isEntityInactive(entity)}"
                >
                  <SelectDocumentTableComponent
                      v-for="(attachable_category, key) in entity.attachable_categories"
                      :key="key"
                      :attachable_category="attachable_category"
                      :selectedDocuments="selectedDocuments[attachable_category.id]"
                      @selectDocuments="(documents) => handleSelectDocuments(attachable_category.id, documents)"
                  />
                </a-collapse-panel>
              </dd-collapse>
            </a-tab-pane>
          </a-tabs>
        </div>
      </div>

      <div class="mt-md flex-row justify-content-end align-items-center gap-md">
        <div
            v-if="countSelectedDocuments > 0"
            class="flex-row align-items-center gap-sm"
        >
          <dd-icon icon="mdi mdi-check" size="md" class="color-black"/>
          <p class="m-0">{{ countSelectedDocuments }} documenten geselecteerd</p>
        </div>
        <dd-button
            type="danger"
            @click="$emit('cancel')"
        >
          Annuleren
        </dd-button>
        <dd-button
            v-if="countSelectedDocuments > 0"
            type="success"
            @click="addSelectedDocuments"
            data-dusk="add-selected-documents"
        >
          Toevoegen
        </dd-button>
      </div>

    </dd-header-card>
  </dd-modal>
</template>

<script>
import moment from 'moment';
import AttachableCategoryDisplayEnum from "~/enums/AttachableCategoryDisplayEnum";
import AttachableCategoryEnum from "~/enums/AttachableCategoryEnum";
import CategoryByClassEnum from "~/enums/CategoryByClassEnum";
import DateFormatEnum from "~/enums/DateFormatEnum";
import AttachableApi from '~/api/attachable';
import AttachableCategoryApi from '~/api/attachable_category';
import CustomerUtils from '~/util/customer';
import SelectDocumentTableComponent from './components/SelectDocumentTableComponent.vue'
import AttachableUtils from "~/util/attachable";

export default {
  name: "SelectDocumentModal.vue",
  emits: ['cancel', 'input'],
  components: {
    SelectDocumentTableComponent
  },
  props:{
    customerId:{
      value: [String, Number]
    },
    visible:{
      value: Boolean
    }
  },
  data(){
    return {
      categoryState: 'KLANT',
      attachableData: [],
      countByCategory: {},
      countSelectedByCategory: { ...AttachableCategoryEnum },
      selectedDocuments: {},
      defaultPanelKey: '',
      loading: true
    }
  },
  async mounted(){
    await this.getCountByCategory();
    this.setCountSelectedByCategory();

    // Get the category by instance, only if it is visible (when there are attachables)
    this.categoryState = Object.entries(AttachableCategoryEnum).reduce((acc, [key, value]) => {
      if (value === CategoryByClassEnum[this.instanceClass] && this.countByCategory[key] > 0) {
        return key;
      }
      return acc;
    }, Object.keys(this.AttachableCategoriesDisplayEnumByCount)[0]);

    await this.loadAttachablesBySelectedCategory();
    this.expandCollapse();
  },
  methods:{
    async handleCategoryChange( category ){
      this.categoryState = category;
      this.attachableData = [];
      await this.loadAttachablesBySelectedCategory();
      this.expandCollapse();
    },
    expandCollapse(){
      // automatically expand panel
      if ((this.categoryState === 'KLANT' || this.categoryState === 'BANKZAKEN') && this.attachableData.length > 0) {
        this.defaultPanelKey = 'attachableEntity.' + this.attachableData[0].customer.id;
      } else if (this.categoryState === 'TENANT' && this.attachableData.length > 0) {
        this.defaultPanelKey = 'attachableEntity.' + this.attachableData[0].entities[0].id;
      } else if (this.instanceId) {
        this.defaultPanelKey = 'attachableEntity.' + this.instanceId;
      } else {
        this.defaultPanelKey = 'noActiveKey';
      }
    },
    async loadAttachablesBySelectedCategory(){
      if (!this.categoryState) return;
      this.loading = true;
      const attachables = await this.getAttachablesByAttachableCategories();
      this.$set(this, 'attachableData', attachables);
      this.loading = false;
    },
    async getAttachableCategories() {
      const { data } = await AttachableCategoryApi.getAllForFamilyByCustomerAndCategory(
          this.customerId,
          AttachableCategoryEnum[this.categoryState],
          [
            'customer',
            'main_categories.attachable_count',
            (this.categoryState !== 'KLANT') ? 'entities.attachable_categories.attachable_count' : '',
            (this.categoryState !== 'KLANT') ? 'entities.attachable_status' : '',
          ]
      );

      return data;
    },
    async getAttachablesByAttachableCategories(){
      const attachableCategories = await this.getAttachableCategories();
      const attachables = await Promise.all(attachableCategories.map(async (data) => {
        let result = {
          customer: data.customer.data,
          entities: []
        };

        const main_cat_attachables = await Promise.all(data.main_categories.data.map(async (category) => {
          if ( category.attachable_count.data.count > 0) return category;
        }));
        const main_cat_data = {
          category_id: data.category_id,
          name: data.customer.data.name,
          id: data.customer.data.id,
          attachable_categories: main_cat_attachables.filter(category => category).flat()
        }
        if (main_cat_data.attachable_categories.length > 0) result.entities.push(main_cat_data);

        data.entities?.data.forEach(entity => {
          let sub_data = {
            name: entity.name,
            id: entity.id,
            active: AttachableUtils.getActiveFromAttachableStatus(entity),
            attachable_categories: entity.attachable_categories.data.map(category => {
              if ( category.attachable_count.data.count > 0) return category;
            }).filter(category => category).flat()
          }
          if ( sub_data.attachable_categories.length > 0) result.entities.push(sub_data);
        });

        if ( result.entities.length > 0 ) {
          result.entities.sort((entity) => entity.active ? -1 : 1);
          return result;
        }
      }));

      return attachables.filter(data => data);
    },
    async getCountByCategory(){
      await Promise.all( Object.entries(this.AttachableCategoryEnum).map(async ([key, category]) => {
        const {data} = await AttachableApi.countForFamilyByCustomerAndCategory(
            this.customerId,
            category
        );
        return {key, count: data};
      }))
          .then( counts => {
            counts.map(({key, count}) => {
              this.$set(this.countByCategory, key, count);
            });
          });
    },
    handleSelectDocuments(attachableCategoryId, documents){
      this.$set(this.selectedDocuments, attachableCategoryId,documents);
      this.setCountSelectedByCategory();
    },
    addSelectedDocuments(){
      const documents = [];
      Object.keys(this.selectedDocuments).forEach(attachableCategoryId => {
        this.selectedDocuments[attachableCategoryId].map(document => {
          documents.push({
            id: document.id,
            filename: document.name.substring(0, Math.min(document.name.length, 50)),
            display_link: document.child_instance?.data?.show_content_link,
          })
        })
      })
      this.$emit('input', documents);

      this.selectedDocuments = {};
      this.setCountSelectedByCategory();
    },
    setCountSelectedByCategory(){
      Object.keys(this.countSelectedByCategory).map( key => {
        this.countSelectedByCategory[key] = false;
      });
      Object.values(this.selectedDocuments).forEach(documents => {
          documents.map(document => {
            const categoryKey = Object.entries(AttachableCategoryEnum).find(entry => entry[1] === document.parent_category)[0];
            this.countSelectedByCategory[categoryKey] = true;
          });
      });
    },
    formatDate(date){
      return moment(date).format(DateFormatEnum.DISPLAY_DATE);
    },
    fullName(customer){
      return CustomerUtils.getCleanName(customer);
    },
    isEntityInactive(entity){
      return entity.active === false;
    },
  },
  computed: {
    noDocuments(){
      const totalDocumentCount = Object.keys(this.countByCategory).reduce((count, key) => count + this.countByCategory[key], 0);
      return totalDocumentCount === 0;
    },
    countSelectedDocuments(){
      return Object.values(this.selectedDocuments).reduce((acc,v) => acc + v.length, 0);
    },
    instanceClass(){
      return this.$route.params?.instanceClass;
    },
    instanceId(){
      return this.$route.params?.instanceId;
    },
    AttachableCategoryEnum() {
      return {...AttachableCategoryEnum, TENANT: 14, AGENCY: 15};
    },
    AttachableCategoryDisplayEnum() {
      return {...AttachableCategoryDisplayEnum, TENANT: "Bedrijf", AGENCY: 'Maatschappijen'};
    },
    AttachableCategoriesDisplayEnumByCount(){
      const displayEnumByCount = {...this.AttachableCategoryDisplayEnum};
      for( let category in displayEnumByCount){
        if ( this.countByCategory[category] === 0) delete displayEnumByCount[category];
      }
      return displayEnumByCount;
    }
  },
};
</script>
<style lang="scss" scoped>
@import "~@/_default.scss";
.category-check{
  position: absolute;
  right: $spacing-md;
}
.attachable-name{
  max-width: 95%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
</style>